import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, CardBody, Form, FormGroup, Label, Input,
  Button, Alert, Spinner
} from 'reactstrap';
import { config } from '../../config/environment';
import { toast } from 'react-toastify';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get flow ID and email from URL/localStorage
  const flowId = searchParams.get('flow') || localStorage.getItem('onboardingFlowId');
  const [email, setEmail] = useState<string>('');

  // Code input state (6 individual digits)
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);

  // Resend timer state
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);

  // Load email from flow data
  useEffect(() => {
    const loadFlowData = async () => {
      if (!flowId) {
        toast.error('No onboarding session found');
        navigate('/getting-started');
        return;
      }

      try {
        const response = await fetch(config.api.onboardingFlowById(flowId));
        if (response.ok) {
          const flow = await response.json();
          setEmail(flow.email);

          // If already verified, skip to plan selection
          const leadResponse = await fetch(`${config.api.leads}/${flow.email}`);
          if (leadResponse.ok) {
            const lead = await leadResponse.json();
            if (lead.emailVerified) {
              toast.success('Email already verified!');
              navigate(`/select-plans?flow=${flowId}`);
            }
          }
        } else {
          toast.error('Failed to load onboarding session');
          navigate('/getting-started');
        }
      } catch (error) {
        console.error('Error loading flow data:', error);
        toast.error('An error occurred. Please try again.');
        navigate('/getting-started');
      }
    };

    loadFlowData();
  }, [flowId, navigate]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0 && !canResend) {
      const timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0) {
      setCanResend(true);
    }
  }, [resendCountdown, canResend]);

  // Handle code input change
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== '') && value) {
      handleVerify(newCode.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only allow 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError(null);

      // Focus last input
      inputRefs.current[5]?.focus();

      // Auto-submit
      handleVerify(pastedData);
    }
  };

  // Verify email code
  const handleVerify = async (codeString: string) => {
    if (codeString.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(config.api.verifyEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: codeString,
        }),
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        setSuccess(true);
        toast.success('Email verified successfully! ✓');

        // Redirect to plan selection after brief delay
        setTimeout(() => {
          navigate(`/select-plans?flow=${flowId}`);
        }, 1500);
      } else {
        // Extract error message from response
        const errorMessage = data.message || 'Invalid verification code';
        setError(errorMessage);

        // Update attempts remaining if provided
        if (errorMessage.includes('attempt')) {
          const match = errorMessage.match(/(\d+)\s+attempt/);
          if (match) {
            setAttemptsRemaining(parseInt(match[1]));
          }
        }

        // Clear code inputs
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();

        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      setError('An error occurred. Please try again.');
      toast.error('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    setResending(true);
    setError(null);

    try {
      const response = await fetch(config.api.resendVerificationCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.sent) {
        toast.success('Verification code sent!');

        // Reset countdown
        setCanResend(false);
        setResendCountdown(60);

        // Reset attempts
        setAttemptsRemaining(3);

        // Clear code inputs
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        const errorMessage = data.message || 'Failed to resend code';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error resending code:', error);
      setError('An error occurred. Please try again.');
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Manual verify button handler
  const handleManualVerify = () => {
    const codeString = code.join('');
    handleVerify(codeString);
  };

  // Format countdown time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!email) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col xs="12" md="10" lg="8" xl="6">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <h4 className="mb-3">Verify Your Email</h4>
                  <p className="text-muted">
                    Enter the 6-digit code we sent to:
                  </p>
                  <p className="fw-bold text-primary">{email}</p>
                </div>

                {error && (
                  <Alert color="danger">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert color="success">
                    <i className="mdi mdi-check-circle me-2"></i>
                    Email verified successfully! Redirecting...
                  </Alert>
                )}

                <Form>
                  <FormGroup>
                    <Label className="mb-3">Verification Code</Label>
                    <div className="d-flex justify-content-center gap-2" onPaste={handlePaste}>
                      {code.map((digit, index) => (
                        <Input
                          key={index}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          innerRef={(el) => { inputRefs.current[index] = el; }}
                          disabled={loading || success}
                          className="text-center fw-bold"
                          style={{
                            fontSize: '1.5rem',
                            width: '3rem',
                            height: '3.5rem',
                            border: '2px solid #dee2e6',
                            borderRadius: '8px',
                          }}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                  </FormGroup>

                  {attemptsRemaining < 3 && !success && (
                    <div className="text-muted small text-center mb-3">
                      <i className="mdi mdi-information-outline me-1"></i>
                      {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
                    </div>
                  )}

                  <div className="mt-4">
                    <Button
                      color="primary"
                      size="lg"
                      block
                      onClick={handleManualVerify}
                      disabled={loading || success || code.some(d => d === '')}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Email'
                      )}
                    </Button>
                  </div>

                  <div className="mt-4 text-center">
                    {!canResend && (
                      <p className="text-muted small mb-2">
                        Resend code in {formatTime(resendCountdown)}
                      </p>
                    )}

                    <Button
                      color="link"
                      onClick={handleResend}
                      disabled={!canResend || resending || success}
                      className="text-decoration-none p-0"
                    >
                      {resending ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-refresh me-1"></i>
                          Resend Code
                        </>
                      )}
                    </Button>
                  </div>
                </Form>

                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted mb-0">
                    Wrong email?{' '}
                    <a
                      href="/getting-started"
                      className="fw-medium text-primary text-decoration-none"
                      onClick={(e) => {
                        e.preventDefault();
                        // Clear all onboarding-related localStorage
                        localStorage.removeItem('onboardingFlowId');
                        localStorage.removeItem('leadData');
                        localStorage.removeItem('selectedPlan');
                        localStorage.removeItem('initialLeadData');
                        localStorage.removeItem('preSelectedPlanId');
                        // Redirect to getting started
                        window.location.href = '/getting-started';
                      }}
                    >
                      Start Over
                    </a>
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EmailVerification;
